"use client";

import type { Ref } from "react";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";
import {
  axisPosition,
  forestAxis,
} from "@/lib/tool-kit/stats-research/forest-plot";
import {
  type Formatter,
  fixed,
  flexible,
} from "@/lib/tool-kit/stats-research/format";

/*
 * A forest plot drawn as a self-contained SVG: colours and fonts are attributes, not
 * page CSS, so the downloaded SVG or PNG looks the same as the one on screen.
 *
 * Everything arrives on the analysis scale (natural logs for ratios); `ratio` makes the
 * axis logarithmic and prints exponentiated values. Squares are sized by weight —
 * area proportional to the study's share — and each pooled estimate is a diamond whose
 * width is its confidence interval.
 */

export interface ForestStudy {
  label: string;
  estimate: number;
  lower: number;
  upper: number;
  /** Share of the total weight, in percent. */
  weight: number;
}

export interface ForestPooled {
  label: string;
  estimate: number;
  lower: number;
  upper: number;
  color: string;
}

const WIDTH = 760;
const ROW = 24;
const TOP = 44;
const PLOT_LEFT = 250;
const PLOT_RIGHT = 520;
const TEXT_X = 536;
const WEIGHT_X = 748;
const MAX_LABEL = 32;
const FONT = "Arial, Helvetica, sans-serif";
const SQUARE_MIN = 4;
const SQUARE_RANGE = 12;

function truncate(label: string): string {
  return label.length > MAX_LABEL ? `${label.slice(0, MAX_LABEL - 1)}…` : label;
}

export function ForestPlot({
  studies,
  pooled,
  ratio,
  axisLabel,
  headings,
  fmt,
  svgRef,
  title,
}: {
  studies: ForestStudy[];
  pooled: ForestPooled[];
  ratio: boolean;
  axisLabel: string;
  headings: { study: string; estimate: string; weight: string };
  fmt: Formatter;
  svgRef: Ref<SVGSVGElement>;
  title: string;
}) {
  const axis = forestAxis(
    [...studies, ...pooled].map((row) => row.lower),
    [...studies, ...pooled].map((row) => row.upper),
    ratio
  );
  const x = (value: number) => axisPosition(value, axis, PLOT_LEFT, PLOT_RIGHT);
  const show = (value: number) =>
    fixed(fmt, ratio ? Math.exp(value) : value, 2);
  const maxWeight = Math.max(...studies.map((study) => study.weight), 1);
  const pooledTop = TOP + (studies.length + 1) * ROW;
  const axisY = pooledTop + pooled.length * ROW + 8;
  const height = axisY + 48;
  const text = {
    fontFamily: FONT,
    fontSize: 12,
    fill: CHART_INK.text,
  } as const;

  return (
    <svg
      aria-label={title}
      height={height}
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${WIDTH} ${height}`}
      width={WIDTH}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill={CHART_INK.background} height={height} width={WIDTH} />
      <g {...text} fontWeight="bold">
        <text x={12} y={24}>
          {headings.study}
        </text>
        <text x={TEXT_X} y={24}>
          {headings.estimate}
        </text>
        <text textAnchor="end" x={WEIGHT_X} y={24}>
          {headings.weight}
        </text>
      </g>
      <line
        stroke={CHART_INK.axis}
        strokeDasharray="4 3"
        x1={x(axis.reference)}
        x2={x(axis.reference)}
        y1={TOP - 10}
        y2={axisY}
      />
      {studies.map((study, i) => {
        const y = TOP + i * ROW + ROW / 2;
        const side =
          SQUARE_MIN + SQUARE_RANGE * Math.sqrt(study.weight / maxWeight);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: studies are positional rows
          <g key={i}>
            <text {...text} x={12} y={y + 4}>
              {truncate(study.label)}
            </text>
            <line
              stroke={CHART_INK.text}
              x1={x(study.lower)}
              x2={x(study.upper)}
              y1={y}
              y2={y}
            />
            <rect
              fill={CHART_COLORS[0]}
              height={side}
              width={side}
              x={x(study.estimate) - side / 2}
              y={y - side / 2}
            />
            <text {...text} x={TEXT_X} y={y + 4}>
              {`${show(study.estimate)} [${show(study.lower)}, ${show(study.upper)}]`}
            </text>
            <text {...text} textAnchor="end" x={WEIGHT_X} y={y + 4}>
              {`${fixed(fmt, study.weight, 1)}%`}
            </text>
          </g>
        );
      })}
      {pooled.map((row, i) => {
        const y = pooledTop + i * ROW + ROW / 2;
        const points = [
          `${x(row.lower)},${y}`,
          `${x(row.estimate)},${y - 7}`,
          `${x(row.upper)},${y}`,
          `${x(row.estimate)},${y + 7}`,
        ].join(" ");
        return (
          <g key={row.label}>
            <text {...text} fontWeight="bold" x={12} y={y + 4}>
              {truncate(row.label)}
            </text>
            <polygon fill={row.color} points={points} />
            <text {...text} fontWeight="bold" x={TEXT_X} y={y + 4}>
              {`${show(row.estimate)} [${show(row.lower)}, ${show(row.upper)}]`}
            </text>
          </g>
        );
      })}
      <line
        stroke={CHART_INK.axis}
        x1={PLOT_LEFT}
        x2={PLOT_RIGHT}
        y1={axisY}
        y2={axisY}
      />
      {axis.ticks.map((tick, i) => (
        <g key={tick}>
          <line
            stroke={CHART_INK.axis}
            x1={x(tick)}
            x2={x(tick)}
            y1={axisY}
            y2={axisY + 5}
          />
          <text
            {...text}
            fill={CHART_INK.muted}
            fontSize={11}
            textAnchor="middle"
            x={x(tick)}
            y={axisY + 18}
          >
            {flexible(fmt, axis.labels[i], 3)}
          </text>
        </g>
      ))}
      <text
        {...text}
        fill={CHART_INK.muted}
        textAnchor="middle"
        x={(PLOT_LEFT + PLOT_RIGHT) / 2}
        y={axisY + 38}
      >
        {axisLabel}
      </text>
    </svg>
  );
}
